import { useEffect, useRef } from 'react';

/**
 * Listens for the global `euromillions:draws-updated` CustomEvent
 * (dispatched from Dashboard whenever a draw is added/edited/deleted)
 * and invokes the provided callback so every analysis page instantly
 * refetches its data.
 *
 * The hook also marks the event object with `__handled = true` so that
 * the App-level fallback (route remount) knows this page already handled
 * the update in-place — preserving scroll position and local UI state.
 *
 * Usage:
 *   useDrawsUpdated(fetchData);
 *
 * Pass the latest fetcher; we keep a ref so the listener never goes stale.
 */
export function useDrawsUpdated(callback) {
  const cbRef = useRef(callback);
  useEffect(() => { cbRef.current = callback; }, [callback]);

  useEffect(() => {
    const handler = (e) => {
      if (e) e.__handled = true;
      try { cbRef.current && cbRef.current(true); } catch (_e) { /* callback error — best-effort refresh */ }
    };
    window.addEventListener('euromillions:draws-updated', handler);
    return () => window.removeEventListener('euromillions:draws-updated', handler);
  }, []);
}

export default useDrawsUpdated;
